import React, { useState } from 'react';
import X from 'lucide-react/dist/esm/icons/x';
import Download from 'lucide-react/dist/esm/icons/download';
import Trash2 from 'lucide-react/dist/esm/icons/trash-2';
import Edit from 'lucide-react/dist/esm/icons/edit';
import Share2 from 'lucide-react/dist/esm/icons/share-2';
import Calendar from 'lucide-react/dist/esm/icons/calendar';
import HardDrive from 'lucide-react/dist/esm/icons/hard-drive';
import Tag from 'lucide-react/dist/esm/icons/tag';
import FileText from 'lucide-react/dist/esm/icons/file-text';
import AlertCircle from 'lucide-react/dist/esm/icons/alert-circle';
import { MyFile } from '../../lib/myFilesService';
import { Button } from '../ui/button';
import { Card } from '../ui/card';

interface FileDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  file: MyFile | null;
  onDelete: (fileId: string) => Promise<void>;
  onDownload: (fileId: string) => Promise<void>;
  formatFileSize: (bytes: number) => string;
  getFileIcon: (fileType: string) => string;
}

export function FileDetailsModal({
  isOpen,
  onClose,
  file,
  onDelete,
  onDownload,
  formatFileSize,
  getFileIcon
}: FileDetailsModalProps) {
  const [downloading, setDownloading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDownload = async () => {
    if (!file) return;
    
    try {
      setDownloading(true);
      setError(null);
      await onDownload(file.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  const handleDelete = async () => {
    if (!file) return;
    
    if (!window.confirm('Are you sure you want to delete this file? This action cannot be undone.')) {
      return;
    }
    
    try {
      setDeleting(true);
      setError(null);
      await onDelete(file.id);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      custom: '#abd3d2',
      legal: '#172241',
      financial: '#4ade80',
      health: '#ef4444',
      pets: '#f97316',
      travel: '#3b82f6',
      digital: '#8b5cf6',
      legacy: '#facc15',
      ndis: '#06b6d4'
    };
    return colors[category] || '#abd3d2';
  };

  if (!isOpen || !file) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <Card className="w-full max-w-2xl mx-4 p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-idecide-dark">File Details</h2>
          <button
            onClick={onClose}
            disabled={downloading || deleting}
            className="text-gray-400 hover:text-gray-600 disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
            <AlertCircle size={16} className="text-red-500" />
            <p className="text-red-600 text-sm">{error}</p>
          </div>
        )}

        {/* File Header */}
        <div className="flex items-start gap-4 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="text-4xl">
            {getFileIcon(file.mime_type || file.file_type || '')}
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-lg font-semibold text-gray-900 mb-1 truncate">
              {file.title}
            </h3>
            <p className="text-sm text-gray-500 mb-2">
              {file.file_name}
            </p>
            <div className="flex items-center gap-4 text-xs text-gray-600">
              <span className="flex items-center gap-1">
                <HardDrive size={12} />
                {formatFileSize(file.file_size)}
              </span>
              <span className="flex items-center gap-1">
                <Calendar size={12} />
                {formatDate(file.created_at)}
              </span>
            </div>
          </div>
        </div>

        {/* File Information Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {/* Basic Information */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Basic Information</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">File Type</label>
                <p className="text-sm text-gray-900">{file.mime_type || file.file_type || 'Unknown'}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Category</label>
                <div className="flex items-center gap-2">
                  <span
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ backgroundColor: getCategoryColor(file.category) }}
                  />
                  <span className="text-sm text-gray-900 capitalize">{file.category}</span>
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Location</label>
                <p className="text-sm text-gray-900">{file.folder_path}</p>
              </div>
              {file.description && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Description</label>
                  <p className="text-sm text-gray-900">{file.description}</p>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div>
            <h4 className="font-medium text-gray-900 mb-3">Metadata</h4>
            <div className="space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-500">Created</label>
                <p className="text-sm text-gray-900">{formatDate(file.created_at)}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-gray-500">Last Modified</label>
                <p className="text-sm text-gray-900">{formatDate(file.updated_at)}</p>
              </div>
              {file.last_accessed && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Last Accessed</label>
                  <p className="text-sm text-gray-900">{formatDate(file.last_accessed)}</p>
                </div>
              )}
              {file.source_module && (
                <div>
                  <label className="text-sm font-medium text-gray-500">Source Module</label>
                  <p className="text-sm text-gray-900 capitalize">{file.source_module}</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Tags */}
        {file.tags && file.tags.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Tags</h4>
            <div className="flex flex-wrap gap-2">
              {file.tags.map((tag, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-idecide-primary bg-opacity-20 text-idecide-dark text-xs rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Sharing Information */}
        {file.shared_with && file.shared_with.length > 0 && (
          <div className="mb-6">
            <h4 className="font-medium text-gray-900 mb-3">Shared With</h4>
            <div className="flex flex-wrap gap-2">
              {file.shared_with.map((user, index) => (
                <span
                  key={index}
                  className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                >
                  {user}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3 pt-4 border-t border-gray-200">
          <Button
            onClick={handleDownload}
            disabled={downloading || deleting}
            className="flex items-center gap-2 bg-idecide-primary hover:bg-idecide-primary-hover text-idecide-dark"
          >
            <Download size={16} />
            {downloading ? 'Downloading...' : 'Download'}
          </Button>
          
          <Button
            onClick={handleDelete}
            disabled={downloading || deleting}
            variant="destructive"
            className="flex items-center gap-2"
          >
            <Trash2 size={16} />
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
          
          <Button
            onClick={onClose}
            disabled={downloading || deleting}
            variant="outline"
            className="flex-1"
          >
            Close
          </Button>
        </div>
      </Card>
    </div>
  );
} 